import { CometChatCalls } from '@cometchat/calls-sdk-javascript';
import { useCallback, useEffect, useId, useState } from 'react';
import { COMETCHAT_CONSTANTS } from '../../AppConstants';
import { receiveCalls, setReceiveCalls } from '../../call-preferences';
import { auth } from '../../firebase';
import { isCometChatAccount } from '../../utils/session-settings';
import Avatar from '../Avatar/Avatar';
import { Dialog } from '../Dialog/Dialog';
import { MdIcon } from '../Icon/MdIcon';
import './ProfileDialog.css';

/**
 * Profile, as a Material 3 dialog: who you are, whether this browser rings, what this account can
 * use, appearance, the build's versions (dev only) and Sign out. Android's ProfileActivity and
 * iOS's ProfileView have the same sections in the same order; web differs only where the
 * platform does:
 *  - Call readiness is about the browser: calls ring only while a Connect tab is open, background
 *    notifications need permission, and the mic / camera permissions decide whether a call starts
 *    cleanly. "Receive calls" off ignores incoming calls in this browser (call-preferences.ts).
 *  - Appearance is fixed: Connect Web stays dark while signed in, because the Calls SDK's screens
 *    have no light palette yet (plan PRE-9, App.tsx).
 */

type Tone = 'ok' | 'off' | 'info';
type Readiness = { title: string; detail: string; tone: Tone; action?: { label: string; run: () => void } };

/** How this account signed in; the same labels as Android and iOS. */
function signInMethod(): string {
  const user = auth.currentUser;
  if (!user) return 'Test user';
  const providers = user.providerData.map(p => p.providerId);
  if (providers.includes('google.com')) return 'Signed in with Google';
  if (providers.includes('apple.com')) return 'Signed in with Apple';
  if (user.isAnonymous) return 'Guest account';
  return 'Signed in with Email';
}

async function permissionState(name: 'microphone' | 'camera'): Promise<PermissionState | 'unknown'> {
  try {
    const status = await navigator.permissions.query({ name: name as PermissionName });
    return status.state;
  } catch {
    // Firefox and Safari don't let a page query these.
    return 'unknown';
  }
}

function mediaRow(label: string, state: PermissionState | 'unknown'): Readiness {
  switch (state) {
    case 'granted':
      return { title: label, detail: 'Allowed', tone: 'ok' };
    case 'denied':
      return {
        title: label,
        detail: "Blocked. Allow it for this site in your browser's settings.",
        tone: 'off',
      };
    default:
      return { title: label, detail: 'Your browser asks when you join a call.', tone: 'info' };
  }
}

function browserName(): string {
  const ua = navigator.userAgent;
  const match =
    /(Edg|OPR|Firefox|Chrome|Version)\/([\d.]+)/.exec(ua) ?? null;
  const engine = match ? (match[1] === 'Edg' ? 'Edge' : match[1] === 'OPR' ? 'Opera' : match[1] === 'Version' ? 'Safari' : match[1]) : 'Browser';
  const version = match?.[2]?.split('.')[0] ?? '';
  const os = /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : '';
  return [engine, version].filter(Boolean).join(' ') + (os ? `, ${os}` : '');
}

export function ProfileDialog({ onClose, onLogout }: { onClose: () => void; onLogout: () => void }) {
  const titleId = useId();
  const user = CometChatCalls.getLoggedInUser();
  const firebaseUser = auth.currentUser;
  const name = user?.name || 'Guest user';
  const uid = user?.uid ?? '';
  const method = signInMethod();
  const isGuest = method === 'Guest account';
  const staff = isCometChatAccount();

  const [ringing, setRinging] = useState(receiveCalls);
  const [readiness, setReadiness] = useState<Readiness[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  const check = useCallback(async () => {
    const rows: Readiness[] = [
      {
        title: 'This tab',
        detail: 'Calls ring here while Connect is open in a tab.',
        tone: 'info',
      },
    ];
    if ('Notification' in window) {
      const p = Notification.permission;
      rows.push(
        p === 'granted'
          ? { title: 'Notifications', detail: 'On. A call shows a notification when this tab is in the background.', tone: 'ok' }
          : p === 'denied'
            ? { title: 'Notifications', detail: "Blocked. Allow them for this site in your browser's settings.", tone: 'off' }
            : {
                title: 'Notifications',
                detail: "Not turned on. You'll only hear a call while this tab is in front.",
                tone: 'off',
                action: {
                  label: 'Turn on',
                  run: () => void Notification.requestPermission().then(() => void check()),
                },
              },
      );
    }
    rows.push(mediaRow('Microphone', await permissionState('microphone')));
    rows.push(mediaRow('Camera', await permissionState('camera')));
    setReadiness(rows);
  }, []);

  useEffect(() => {
    void check();
    // Settings changed in another window come back with focus.
    window.addEventListener('focus', check);
    return () => window.removeEventListener('focus', check);
  }, [check]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  const copy = (text: string, done: string) => {
    void navigator.clipboard?.writeText(text).then(() => setToast(done), () => setToast("Couldn't copy"));
  };

  const versions: [string, string][] = [
    ['Environment', COMETCHAT_CONSTANTS.IS_STAGING ? 'Staging' : 'Production'],
    ['App ID', COMETCHAT_CONSTANTS.APP_ID],
    ['Region', COMETCHAT_CONSTANTS.REGION],
    ['App version', __APP_VERSION__],
    ['Calls SDK', __CALLS_SDK_VERSION__],
    ['Chat SDK', __CHAT_SDK_VERSION__],
    ['Chat UI Kit', __UIKIT_VERSION__],
    ['Browser', browserName()],
  ];

  return (
    <Dialog bare labelledBy={titleId} onClose={onClose}>
      <div className="profile">
        <header className="profile__bar">
          <h2 className="profile__bar-title" id={titleId}>
            Profile
          </h2>
          <button type="button" className="md-icon-button" aria-label="Close" onClick={onClose}>
            <MdIcon name="close" />
          </button>
        </header>

        <div className="profile__body">
          <section className="profile__identity" aria-label="Account">
            <Avatar size={88} name={user?.name || ''} imageUrl={user?.avatar} />
            <p className="profile__name">{name}</p>
            <span className="profile__chip">{method}</span>
          </section>

          <div className="profile__card">
            <div className="profile__row profile__row--stacked">
              <span className="profile__label">UID</span>
              <span className="profile__value profile__value--mono">{uid || '—'}</span>
              {uid && (
                <button
                  type="button"
                  className="md-icon-button profile__row-action"
                  aria-label="Copy UID"
                  onClick={() => copy(uid, 'UID copied')}>
                  <MdIcon name="content_copy" size={20} />
                </button>
              )}
            </div>
            {firebaseUser?.email && (
              <div className="profile__row profile__row--stacked">
                <span className="profile__label">Email</span>
                <span className="profile__value">{firebaseUser.email}</span>
              </div>
            )}
          </div>
          {isGuest && (
            <p className="profile__note">
              You're signed in as a guest. Signing out ends this guest account, and its call
              history can't be reopened.
            </p>
          )}

          <h3 className="profile__section">Call readiness</h3>
          <div className="profile__card">
            <label className="profile__switch-row">
              <span className="profile__switch-text">
                <span className="profile__switch-title">Receive calls</span>
                <span className="profile__label">
                  {ringing
                    ? 'Ring this browser when someone calls you.'
                    : "Calls to you won't ring this browser. Your other devices still ring."}
                </span>
              </span>
              <input
                type="checkbox"
                role="switch"
                className="profile__switch"
                id="profile-receive-calls"
                checked={ringing}
                onChange={e => {
                  setReceiveCalls(e.target.checked);
                  setRinging(e.target.checked);
                }}
              />
            </label>
          </div>
          {ringing && (
            <div className="profile__card profile__card--spaced">
              {readiness.map(r => (
                <div className="profile__row" key={r.title}>
                  <span className={`profile__dot profile__dot--${r.tone}`} aria-hidden="true" />
                  <span className="profile__row-text">
                    <span className="profile__value">{r.title}</span>
                    <span className="profile__label">{r.detail}</span>
                  </span>
                  {r.action && (
                    <button type="button" className="profile__text-button" onClick={r.action.run}>
                      {r.action.label}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <h3 className="profile__section">Features</h3>
          <div className="profile__card">
            <div className="profile__row">
              <span className="profile__row-text">
                <span className="profile__value">Recording and transcripts</span>
                <span className="profile__label">
                  {staff
                    ? 'Record a meeting, turn on captions, keep a transcript.'
                    : 'Available with a @cometchat.com account'}
                </span>
              </span>
              {staff ? (
                <span className="profile__available">Available</span>
              ) : (
                <span className="profile__lock">
                  <MdIcon name="lock" size={20} />
                </span>
              )}
            </div>
          </div>

          <h3 className="profile__section">Appearance</h3>
          <p className="profile__note profile__note--flush">
            Dark while you're signed in: the call screens don't have a light theme yet.
          </p>

          {import.meta.env.DEV && (
            <>
              <h3 className="profile__section">Environment &amp; versions</h3>
              <div className="profile__card">
                {versions.map(([label, value]) => (
                  <div className="profile__row profile__row--inline" key={label}>
                    <span className="profile__label">{label}</span>
                    <span className="profile__value">{value || '—'}</span>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="profile__text-button profile__copy"
                onClick={() =>
                  copy(
                    [['UID', uid], ...versions]
                      .filter(([, v]) => v)
                      .map(([l, v]) => `${l}: ${v}`)
                      .join('\n'),
                    'Details copied',
                  )
                }>
                <MdIcon name="content_copy" size={18} />
                Copy details
              </button>
            </>
          )}

          {confirmSignOut ? (
            <div className="profile__confirm" role="alertdialog" aria-label="Sign out of this guest account?">
              <p className="profile__value">Sign out of this guest account?</p>
              <p className="profile__label">Its call history can't be reopened.</p>
              <div className="profile__confirm-actions">
                <button type="button" className="profile__text-button" onClick={() => setConfirmSignOut(false)}>
                  Cancel
                </button>
                <button type="button" className="profile__text-button profile__text-button--danger" onClick={onLogout}>
                  Sign out
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="profile__sign-out"
              onClick={() => (isGuest ? setConfirmSignOut(true) : onLogout())}>
              <MdIcon name="logout" size={20} />
              Sign out
            </button>
          )}
        </div>
      </div>
      {toast && (
        <div className="md-snackbar" role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </Dialog>
  );
}
